"use client"

import * as React from "react"
import { animate, utils, type JSAnimation } from "animejs"
import { cn } from "cn"

import { useDirection } from "./direction"
import { distance, duration, ease, prefersReducedMotion } from "../../lib/motion"

const OPEN_MS = duration.slow
const CLOSE_MS = duration.base

function SidePanel({
  open,
  appear = true,
  className,
  contentClassName,
  children,
}: {
  open: boolean
  /** false shows a panel that is open on mount in place, e.g. one that stays open across pages. */
  appear?: boolean
  className?: string
  contentClassName?: string
  children: React.ReactNode
}) {
  const [rendered, setRendered] = React.useState(open)
  if (open && !rendered) setRendered(true)
  const root = React.useRef<HTMLElement>(null)
  const content = React.useRef<HTMLDivElement>(null)
  const direction = useDirection()
  const skipFirstOpen = React.useRef(!appear && open)

  React.useEffect(() => {
    const element = root.current
    const inner = content.current
    if (!element || !inner) return
    const shift = (direction === "rtl" ? -1 : 1) * distance.lg
    const running: JSAnimation[] = []
    const reduced = prefersReducedMotion()

    if (open) {
      if (reduced || skipFirstOpen.current) return
      const target = inner.offsetWidth
      utils.set(element, { width: 0 })
      utils.set(inner, { opacity: 0, translateX: shift })
      running.push(
        animate(element, { width: target, duration: OPEN_MS, ease: ease.inOut, onComplete: () => element.style.removeProperty("width") }),
        animate(inner, { opacity: 1, translateX: 0, duration: OPEN_MS, delay: duration.instant, ease: ease.standard })
      )
    } else {
      skipFirstOpen.current = false
      const finish = () => setRendered(false)
      if (reduced) {
        const timer = window.setTimeout(finish, 0)
        return () => window.clearTimeout(timer)
      }
      running.push(
        animate(element, { width: 0, duration: CLOSE_MS, ease: ease.inOut, onComplete: finish }),
        animate(inner, { opacity: 0, translateX: shift, duration: CLOSE_MS * 0.8, ease: ease.exit })
      )
    }
    return () => running.forEach((animation) => animation.pause())
  }, [open, rendered, direction])

  if (!rendered) return null

  return (
    <aside ref={root} data-slot="side-panel" data-state={open ? "open" : "closed"} className={cn("overflow-hidden", className)}>
      <div ref={content} className={contentClassName}>
        {children}
      </div>
    </aside>
  )
}

export { SidePanel }
